import React, { useState, useEffect, useCallback } from 'react';
import Layout from './Layout';
import { authService } from '../services/authService';
import { atsConnectionService } from '../services/atsConnectionService';

// The screen a tenant configures their own ATS connection through -- a NEW
// surface, not a change to IntegrationConfig.jsx/IntegrationList.jsx (path 2,
// the API/worker connector, which has never run anywhere). Keeping the two
// separate is what lets path 2 later be completed or retired as a clean cut.
//
// No sync-time field on purpose: the nightly schedule is one fixed time for
// everyone, which is why sync_frequency/sync_time/include_weekends were left
// out of the data model (Task 5's PTA design).

const emptyForm = { endpoint: '', namespace: '', username: '', password: '' };

export default function AtsConnectionForm() {
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [hasSecret, setHasSecret] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState('');

  useEffect(() => {
    let cancelled = false;
    atsConnectionService
      .get()
      .then((data) => {
        if (cancelled) return;
        setForm({
          endpoint: data.endpoint || '',
          namespace: data.namespace || '',
          username: data.username || '',
          password: '',
        });
        setHasSecret(!!data.has_secret);
        setIsEnabled(!!data.is_enabled);
        setNotConfigured(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err && err.status === 404) {
          setNotConfigured(true);
        } else {
          setLoadError((err && err.message) || 'Could not load the connection.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = useCallback((field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);
      setSaveError('');
      setSaveMessage('');
      try {
        const body = {
          endpoint: form.endpoint,
          namespace: form.namespace,
          username: form.username,
        };
        // Omitting the key entirely (rather than sending "") is what leaves the
        // stored password byte-identical -- the API never has to tell
        // "unchanged" from "clear it".
        if (form.password !== '') {
          body.password = form.password;
        }
        await atsConnectionService.save(body);
        setHasSecret(hasSecret || form.password !== '');
        setForm((prev) => ({ ...prev, password: '' }));
        setNotConfigured(false);
        setSaveMessage('Saved.');
      } catch (err) {
        setSaveError((err && err.message) || 'Could not save the connection.');
      } finally {
        setSaving(false);
      }
    },
    [form, hasSecret]
  );

  const handleToggleEnabled = useCallback(async () => {
    setEnabling(true);
    setEnableError('');
    try {
      const result = await atsConnectionService.setEnabled(!isEnabled);
      setIsEnabled(!!result.is_enabled);
    } catch (err) {
      setEnableError((err && err.message) || 'Could not update the nightly import.');
    } finally {
      setEnabling(false);
    }
  }, [isEnabled]);

  // authService.getUser() never throws (see its own doc) and is synchronous --
  // read straight from sessionStorage, same as DataPage/SignalSourcesPage do
  // before handing it to Layout. Layout works with no user too (it falls back
  // to generic labels), so a null read here degrades, it does not break.
  const user = authService.getUser();

  if (loading) {
    return (
      <Layout user={user}>
        <div className="max-w-xl p-6">Loading your ATS connection…</div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-xl p-6">
        <h1 className="text-xl font-semibold text-gray-900">ATS connection</h1>
        <p className="mt-1 text-sm text-gray-600">
          Connect your iSmartRecruit account so SignalCue can import your data every night.
        </p>

        {loadError && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {loadError}
          </p>
        )}
        {notConfigured && !loadError && (
          <p className="mt-3 text-sm text-gray-600">No ATS connection is set up yet.</p>
        )}

        <form onSubmit={handleSave} className="mt-4 space-y-4">
          <div>
            <label htmlFor="ats-endpoint" className="block text-sm font-medium text-gray-700">
              Server
            </label>
            <input
              id="ats-endpoint"
              type="text"
              value={form.endpoint}
              onChange={handleChange('endpoint')}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              The server address iSmartRecruit gave your agency (looks like project:region:instance).
            </p>
          </div>

          <div>
            <label htmlFor="ats-namespace" className="block text-sm font-medium text-gray-700">
              Database
            </label>
            <input
              id="ats-namespace"
              type="text"
              value={form.namespace}
              onChange={handleChange('namespace')}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              The database iSmartRecruit assigned to your agency.
            </p>
          </div>

          <div>
            <label htmlFor="ats-username" className="block text-sm font-medium text-gray-700">
              Username
            </label>
            <input
              id="ats-username"
              type="text"
              value={form.username}
              onChange={handleChange('username')}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              The username iSmartRecruit uses to sign you in to that database.
            </p>
          </div>

          <div>
            <label htmlFor="ats-password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="ats-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange('password')}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              The password iSmartRecruit uses to sign you in to that database.
            </p>
            {hasSecret && (
              <p className="mt-1 text-xs text-gray-600">
                A password is stored for this connection. Leave this blank to keep it — type a new
                one only to replace it.
              </p>
            )}
          </div>

          {saveError && (
            <p className="text-sm text-red-600" role="alert">
              {saveError}
            </p>
          )}
          {saveMessage && !saveError && <p className="text-sm text-green-700">{saveMessage}</p>}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save connection'}
            </button>

            {/* No "Test connection" button here on purpose. signalcue-api's Cloud
                SQL mount (infra/terraform/api.tf) only attaches the shared app
                instance -- the tenant's ATS instance is mounted onto the ingest
                Job (split_jobs.tf) only, not this service. A dial to
                /cloudsql/<instance> from here can never reach a socket, so the
                button would always fail, in dev and staging alike, whether the
                tenant's credentials are right or wrong -- indistinguishable from
                a real credential problem. Hidden, not removed: atsConnectionService.test
                and the server's /api/ats-connection/test route are left intact
                for when the instance is mounted. Don't re-add this button
                without fixing the mount first. */}
          </div>
        </form>

        <div className="mt-6 border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-700">
            {isEnabled
              ? 'Nightly import is on. SignalCue imports your ATS data every night.'
              : 'Nightly import is off. Turn it on once the connection details are saved.'}
          </p>
          <button
            type="button"
            onClick={handleToggleEnabled}
            disabled={enabling || notConfigured}
            className="mt-2 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            {enabling
              ? 'Updating…'
              : isEnabled
                ? 'Disable nightly import'
                : 'Enable nightly import'}
          </button>
          {enableError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {enableError}
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
